import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ImprimirRespuesta, PrintJobLocalPendiente } from "@tentacion/shared";
import { apiFetch } from "../api";
import { useAuth } from "../auth";
import { imprimirTicketWindows, abrirCajonWindows } from "../impresionWindows";

export interface PrintJobItem {
  id: string;
  sucursalId: string;
  impresoraId: string;
  pedidoId?: string | null;
  tipo: "COMANDA" | "CUENTA" | "PRUEBA";
  estado: "PENDIENTE" | "IMPRESO" | "FALLIDO";
  intentos: number;
  maxIntentos: number;
  errorLog?: string | null;
  creadoEn: string;
  procesadoEn?: string | null;
  impresora?: {
    nombre: string;
    ip: string;
  };
}

export interface EstadoColaImpresion {
  pendientes: number;
  fallidos: number;
  impresos: number;
  estadoGeneral: "OK" | "PROCESANDO" | "ALERTA";
  ultimosJobs: PrintJobItem[];
}

export function useEstadoColaImpresion() {
  const { usuarioId } = useAuth();
  return useQuery({
    queryKey: ["impresion", "cola"],
    queryFn: () => apiFetch<EstadoColaImpresion>("/impresion/cola"),
    enabled: Boolean(usuarioId),
    refetchInterval: 5000,
  });
}

export function useReimprimirJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string) => apiFetch("/impresion/reimprimir", { method: "POST", body: { jobId } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["impresion", "cola"] });
    },
  });
}

/**
 * Cancela a mano uno o varios trabajos atascados en la cola -- ej. todos
 * los que quedaron pegados por una impresora sin papel. Los marca como
 * FALLIDO de inmediato (nunca toca uno que ya se imprimió de verdad).
 */
export function useCancelarJobsImpresion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (jobIds: string[]) =>
      apiFetch<{ cancelados: number }>("/impresion/cola/cancelar", { method: "POST", body: { jobIds } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["impresion", "cola"] });
    },
  });
}

export function useReimprimirFactura() {
  return useMutation({
    mutationFn: (args: string | { facturaId: string; impresoraId?: string }) => {
      const facturaId = typeof args === "string" ? args : args.facturaId;
      const impresoraId = typeof args === "string" ? undefined : args.impresoraId;
      return apiFetch<ImprimirRespuesta>(`/impresion/reimprimir-factura/${facturaId}`, {
        method: "POST",
        body: impresoraId ? { impresoraId } : undefined,
      });
    },
    onSuccess: imprimirTicketClienteSiAplica,
  });
}

export function useReenviarComandaFactura() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (facturaId: string) =>
      apiFetch(`/impresion/reenviar-comanda/${facturaId}`, { method: "POST" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["impresion", "cola"] });
    },
  });
}

export function useProbarImpresora() {
  return useMutation({
    mutationFn: (impresoraId: string) =>
      apiFetch<{ exito: boolean; mensaje: string }>(`/impresion/probar-impresora/${impresoraId}`, {
        method: "POST",
      }),
  });
}

/**
 * Si la impresora de destino es SISTEMA_LOCAL (USB/driver, sin IP de red),
 * el backend no pudo imprimir por TCP y en su lugar devuelve `ticketCliente`
 * listo para imprimirse desde este mismo navegador.
 */
function imprimirTicketClienteSiAplica(respuesta: ImprimirRespuesta): void {
  if (!respuesta.ticketCliente) return;
  const t = respuesta.ticketCliente;
  imprimirTicketWindows({
    dian: t.dian,
    titulo: t.titulo,
    subtitulo: t.subtitulo,
    mesaNumero: t.mesaNumero,
    meseraNombre: t.meseraNombre,
    fecha: t.fecha ? new Date(t.fecha) : undefined,
    items: t.items,
    subtotal: t.subtotal,
    descuento: t.descuento,
    propina: t.propina,
    total: t.total,
    pagos: t.pagos,
    clienteNombre: t.clienteNombre,
    clienteDocumento: t.clienteDocumento,
    notasGenerales: t.notasGenerales,
    logoUrl: t.logoUrl,
    imprimirLogoTicket: t.imprimirLogoTicket,
    encabezadoTicket: t.encabezadoTicket,
    pieTicket: t.pieTicket,
    sucursalNombre: t.sucursalNombre,
  });
}

export function useImprimirPrecuenta() {
  return useMutation({
    mutationFn: (args: string | { mesaId: string; impresoraId?: string }) => {
      const mesaId = typeof args === "string" ? args : args.mesaId;
      const impresoraId = typeof args === "string" ? undefined : args.impresoraId;
      return apiFetch<ImprimirRespuesta>(`/impresion/precuenta/${mesaId}`, {
        method: "POST",
        body: impresoraId ? { impresoraId } : undefined,
      });
    },
    onSuccess: imprimirTicketClienteSiAplica,
  });
}

export function useImprimirCierreCaja() {
  return useMutation({
    mutationFn: (args: string | { turnoCajaId: string; impresoraId?: string }) => {
      const turnoCajaId = typeof args === "string" ? args : args.turnoCajaId;
      const impresoraId = typeof args === "string" ? undefined : args.impresoraId;
      return apiFetch<ImprimirRespuesta>(`/impresion/cierre-caja/${turnoCajaId}`, {
        method: "POST",
        body: impresoraId ? { impresoraId } : undefined,
      });
    },
    onSuccess: imprimirTicketClienteSiAplica,
  });
}

export function useImprimirGastoCaja() {
  return useMutation({
    mutationFn: (args: string | { gastoId: string; impresoraId?: string }) => {
      const gastoId = typeof args === "string" ? args : args.gastoId;
      const impresoraId = typeof args === "string" ? undefined : args.impresoraId;
      return apiFetch<ImprimirRespuesta>(`/impresion/gasto-caja/${gastoId}`, {
        method: "POST",
        body: impresoraId ? { impresoraId } : undefined,
      });
    },
    onSuccess: imprimirTicketClienteSiAplica,
  });
}

export function useAbrirCajon() {
  return useMutation({
    mutationFn: (impresoraId?: string) =>
      apiFetch<ImprimirRespuesta>("/impresion/abrir-cajon", {
        method: "POST",
        body: impresoraId ? { impresoraId } : undefined,
      }),
    onSuccess: (data) => {
      if (data?.ticketCliente && data.ticketCliente.titulo === "Abrir Cajón") {
        abrirCajonWindows();
      }
    },
  });
}


/**
 * Sondea las comandas pendientes de imprimir desde ESTE navegador (impresoras
 * SISTEMA_LOCAL). Debe montarse una sola vez en un layout compartido
 * (BarraSuperior) para que el dispositivo conectado a esa impresora las
 * recoja mientras tenga la app abierta.
 */
export function usePendientesImpresionLocal() {
  const { usuarioId } = useAuth();
  return useQuery({
    queryKey: ["impresion", "pendientes-local"],
    queryFn: () => apiFetch<PrintJobLocalPendiente[]>("/impresion/comandas-pendientes-local"),
    enabled: Boolean(usuarioId),
    refetchInterval: 2000,
  });
}

export function useConfirmarImpresionLocal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string) =>
      apiFetch(`/impresion/comandas-pendientes-local/${jobId}/confirmar`, { method: "POST" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["impresion", "pendientes-local"] });
    },
  });
}
